import { useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { publicMessage } from "../../shared/lib/errors";
import { ThemeToggle } from "../../shared/ui/ThemeProvider";
import { useSession } from "./SessionProvider";

type AuthMode = "login" | "signup" | "reset";

export function AuthPage() {
  const auth = useSession();
  const [mode, setMode] = useState<AuthMode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  if (auth.recovery) return <Navigate to="/recovery" replace />;
  if (auth.status === "authenticated") return <Navigate to="/inicio" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      if (mode === "login") await auth.signIn(email, password);
      if (mode === "signup") {
        await auth.signUp(email, password);
        setMessage("Conta criada. Confirme seu e-mail se essa opção estiver ativa.");
      }
      if (mode === "reset") {
        await auth.requestPasswordReset(email);
        setMessage("Link enviado. Confira seu e-mail.");
      }
    } catch (error: unknown) {
      setMessage(publicMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-shell">
      <section className="auth-card" aria-labelledby="auth-title">
        <div className="auth-toolbar">
          <div className="brand-row">
            <span className="brand-mark">B</span>
            <strong>Base Hormonal</strong>
          </div>
          <ThemeToggle />
        </div>
        <h1 id="auth-title">
          {mode === "login"
            ? "Entre na sua conta"
            : mode === "signup"
              ? "Crie sua conta"
              : "Recupere sua senha"}
        </h1>
        <div className="segmented" aria-label="Opções de autenticação">
          <button type="button" aria-pressed={mode === "login"} onClick={() => setMode("login")}>
            Entrar
          </button>
          <button type="button" aria-pressed={mode === "signup"} onClick={() => setMode("signup")}>
            Criar conta
          </button>
          <button type="button" aria-pressed={mode === "reset"} onClick={() => setMode("reset")}>
            Recuperar
          </button>
        </div>
        <form onSubmit={submit}>
          <label htmlFor="auth-email">E-mail</label>
          <input
            id="auth-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          {mode !== "reset" ? (
            <>
              <label htmlFor="auth-password">Senha</label>
              <input
                id="auth-password"
                type="password"
                minLength={6}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </>
          ) : null}
          <button className="primary" disabled={busy} type="submit">
            {busy
              ? "Aguarde…"
              : mode === "login"
                ? "Entrar"
                : mode === "signup"
                  ? "Criar conta"
                  : "Enviar link"}
          </button>
          <p className="form-message" aria-live="polite">
            {message}
          </p>
        </form>
      </section>
    </main>
  );
}

export function RecoveryPage() {
  const auth = useSession();
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  if (!auth.recovery && auth.status === "anonymous") return <Navigate to="/auth" replace />;
  return (
    <main className="auth-shell">
      <section className="auth-card">
        <div className="auth-toolbar">
          <div className="brand-row">
            <span className="brand-mark">B</span>
            <strong>Base Hormonal</strong>
          </div>
          <ThemeToggle />
        </div>
        <h1>Defina sua nova senha</h1>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void auth
              .updatePassword(password)
              .catch((error: unknown) => setMessage(publicMessage(error)));
          }}
        >
          <label htmlFor="new-password">Nova senha</label>
          <input
            id="new-password"
            type="password"
            minLength={6}
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <button className="primary" type="submit">
            Salvar senha
          </button>
          <p className="form-message" aria-live="polite">
            {message}
          </p>
        </form>
      </section>
    </main>
  );
}
